import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AlertCircle, Play, Trash2, Pencil, Clock, Zap, Package, AlertTriangle } from 'lucide-react';
import {
  fetchContinuousTesting,
  createContinuousTesting,
  updateContinuousTesting,
  deleteContinuousTesting,
  triggerContinuousTesting,
} from '@/api/admin';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import type { ContinuousTestingConfig } from '@/types/admin';

interface EditingConfig {
  id: number;
  project_name: string;
  interval_hours: number;
  enabled: boolean;
  discord_webhook_url: string;
}

export function AdminContinuousTestingPage() {
  const queryClient = useQueryClient();

  // Create form state
  const [projectId, setProjectId] = useState<number | null>(null);
  const [intervalHours, setIntervalHours] = useState(6);
  const [enabled, setEnabled] = useState(true);
  const [discordWebhook, setDiscordWebhook] = useState('');

  // Edit dialog state
  const [editingConfig, setEditingConfig] = useState<EditingConfig | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-continuous-testing'],
    queryFn: fetchContinuousTesting,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createContinuousTesting({
        project_id: projectId!,
        interval_hours: intervalHours,
        enabled,
        discord_webhook_url: discordWebhook || null,
      }),
    onSuccess: () => {
      toast.success('Configuration created');
      setProjectId(null);
      setIntervalHours(6);
      setEnabled(true);
      setDiscordWebhook('');
      queryClient.invalidateQueries({ queryKey: ['admin-continuous-testing'] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to create'),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: { id: number; interval_hours: number; enabled: boolean; discord_webhook_url: string | null }) =>
      updateContinuousTesting(payload.id, {
        interval_hours: payload.interval_hours,
        enabled: payload.enabled,
        discord_webhook_url: payload.discord_webhook_url,
      }),
    onSuccess: () => {
      toast.success('Configuration updated');
      setEditDialogOpen(false);
      setEditingConfig(null);
      queryClient.invalidateQueries({ queryKey: ['admin-continuous-testing'] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to update'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteContinuousTesting(id),
    onSuccess: () => {
      toast.success('Configuration deleted');
      queryClient.invalidateQueries({ queryKey: ['admin-continuous-testing'] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to delete'),
  });

  const triggerMutation = useMutation({
    mutationFn: (id: number) => triggerContinuousTesting(id),
    onSuccess: (data: any) => {
      if (data.builds_queued === 0) {
        toast.info(data.message || 'No builds to trigger');
      } else {
        toast.success(data.message || 'Test run triggered');
      }
      queryClient.invalidateQueries({ queryKey: ['admin-continuous-testing'] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to trigger'),
  });

  const configs = data?.configs || [];
  const availableProjects = data?.available_projects || [];

  // Compute stats
  const totalConfigs = configs.length;
  const enabledConfigs = configs.filter((c) => c.config.enabled).length;
  const totalDevBuilds = configs.reduce((sum, c) => sum + (c.dev_builds_count ?? 0), 0);
  const dueNowCount = configs.filter((c) => c.is_due).length;

  const openEditDialog = (config: ContinuousTestingConfig) => {
    setEditingConfig({
      id: config.id,
      project_name: config.project_name || `Project ${config.project_id}`,
      interval_hours: config.interval_hours,
      enabled: config.enabled,
      discord_webhook_url: config.discord_webhook_url || '',
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (!editingConfig) return;
    updateMutation.mutate({
      id: editingConfig.id,
      interval_hours: editingConfig.interval_hours,
      enabled: editingConfig.enabled,
      discord_webhook_url: editingConfig.discord_webhook_url || null,
    });
  };

  const handleDelete = (id: number, projectName: string) => {
    if (confirm(`Remove continuous testing for "${projectName}"?`)) {
      deleteMutation.mutate(id);
    }
  };

  const handleTrigger = (id: number, projectName: string, devBuildsCount: number) => {
    const message = devBuildsCount > 0
      ? `Trigger test run for "${projectName}"? This will rebuild ${devBuildsCount} development build(s).`
      : `Trigger test run for "${projectName}"? No active development builds found.`;
    if (confirm(message)) {
      triggerMutation.mutate(id);
    }
  };

  const formatTimeUntil = (seconds: number | null | undefined) => {
    if (seconds === null || seconds === undefined) return '—';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Continuous Testing</h1>
          <p className="text-sm text-muted-foreground">
            Automatically rebuild developer branches at regular intervals
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Projects</p>
                <p className="text-2xl font-semibold">{totalConfigs}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Zap className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Enabled</p>
                <p className="text-2xl font-semibold">{enabledConfigs}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Clock className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Dev Builds</p>
                <p className="text-2xl font-semibold">{totalDevBuilds}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Due Now</p>
                <p className="text-2xl font-semibold">{dueNowCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create New Configuration */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Add Project</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
            <div className="space-y-2">
              <Label>Project</Label>
              <Select value={projectId ? String(projectId) : ''} onValueChange={(v) => setProjectId(Number(v))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {availableProjects.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Interval (hours)</Label>
              <Input
                type="number"
                value={intervalHours}
                onChange={(e) => setIntervalHours(Number(e.target.value))}
                min={1}
                max={168}
              />
            </div>
            <div className="space-y-2">
              <Label>Discord Webhook</Label>
              <Input
                placeholder="Optional"
                value={discordWebhook}
                onChange={(e) => setDiscordWebhook(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2 pb-1">
              <Switch checked={enabled} onCheckedChange={setEnabled} id="new-enabled" />
              <Label htmlFor="new-enabled">Enabled</Label>
            </div>
            <Button onClick={() => projectId && createMutation.mutate()} disabled={!projectId || createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Add Project'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>Failed to load configurations.</AlertDescription>
        </Alert>
      )}

      {/* Configurations Table */}
      <Card>
        <CardHeader>
          <CardTitle>Configured Projects</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : configs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No projects configured yet. Add one above.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b">
                    <th className="py-3 px-2 font-medium">Project</th>
                    <th className="py-3 px-2 font-medium">Status</th>
                    <th className="py-3 px-2 font-medium">Interval</th>
                    <th className="py-3 px-2 font-medium">Dev Builds</th>
                    <th className="py-3 px-2 font-medium">Last Run</th>
                    <th className="py-3 px-2 font-medium">Next Run</th>
                    <th className="py-3 px-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {configs.map(({ config, dev_builds_count, is_due, time_until_next_run }) => (
                    <tr key={config.id} className="border-b hover:bg-muted/50">
                      <td className="py-3 px-2">
                        <span className="font-medium">{config.project_name || `Project ${config.project_id}`}</span>
                      </td>
                      <td className="py-3 px-2">
                        {config.enabled ? (
                          <Badge variant="default" className="bg-green-100 text-green-800 hover:bg-green-100">
                            Enabled
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Disabled</Badge>
                        )}
                      </td>
                      <td className="py-3 px-2">{config.interval_hours}h</td>
                      <td className="py-3 px-2">
                        <Badge variant="outline">{dev_builds_count ?? 0}</Badge>
                      </td>
                      <td className="py-3 px-2 text-muted-foreground">
                        {config.last_run_at ? new Date(config.last_run_at).toLocaleString() : '—'}
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">
                            {config.next_run_at ? new Date(config.next_run_at).toLocaleString() : '—'}
                          </span>
                          {is_due && <Badge variant="destructive" className="text-xs">Due</Badge>}
                          {!is_due && time_until_next_run !== null && time_until_next_run !== undefined && (
                            <span className="text-xs text-muted-foreground">({formatTimeUntil(time_until_next_run)})</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(config)}
                            title="Edit configuration"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleTrigger(
                              config.id,
                              config.project_name || `Project ${config.project_id}`,
                              dev_builds_count ?? 0
                            )}
                            disabled={triggerMutation.isPending}
                            title="Trigger test run"
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDelete(config.id, config.project_name || `Project ${config.project_id}`)}
                            disabled={deleteMutation.isPending}
                            title="Delete configuration"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Configuration</DialogTitle>
            <DialogDescription>
              Modify continuous testing settings for {editingConfig?.project_name}
            </DialogDescription>
          </DialogHeader>
          {editingConfig && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-interval">Test Interval (hours)</Label>
                <Input
                  id="edit-interval"
                  type="number"
                  value={editingConfig.interval_hours}
                  onChange={(e) =>
                    setEditingConfig({ ...editingConfig, interval_hours: Number(e.target.value) })
                  }
                  min={1}
                  max={168}
                />
                <p className="text-xs text-muted-foreground">How often to rebuild all developer branches (1-168 hours)</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-webhook">Discord Webhook URL</Label>
                <Input
                  id="edit-webhook"
                  type="url"
                  placeholder="https://discord.com/api/webhooks/..."
                  value={editingConfig.discord_webhook_url}
                  onChange={(e) =>
                    setEditingConfig({ ...editingConfig, discord_webhook_url: e.target.value })
                  }
                />
                <p className="text-xs text-muted-foreground">Optional. Leave empty to use the global webhook.</p>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  id="edit-enabled"
                  checked={editingConfig.enabled}
                  onCheckedChange={(checked) => setEditingConfig({ ...editingConfig, enabled: checked })}
                />
                <Label htmlFor="edit-enabled">Enable continuous testing</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
